import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Copy, ExternalLink, QrCode, Code, Settings, Eye, GripVertical, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' }
];

const DEFAULT_FIELDS = [
  { id: 'name', type: 'text', label: 'Full Name', required: true, placeholder: 'Enter your name' },
  { id: 'phone', type: 'tel', label: 'Phone Number', required: true, placeholder: '+91 98765 43210' },
  { id: 'email', type: 'email', label: 'Email', required: false, placeholder: 'your@email.com' }
];

export default function LeadCapture() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [embedModal, setEmbedModal] = useState(null);
  const [activeTab, setActiveTab] = useState('forms');

  const fetchForms = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/leads/capture/forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setForms(data.forms || []);
    } catch (error) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const deleteForm = async (formId) => {
    if (!window.confirm('Delete this form?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_URL}/api/leads/capture/forms/${formId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Form deleted');
      fetchForms();
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  const openEmbedModal = async (form) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/leads/capture/forms/${form.id}/embed-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEmbedModal({ ...form, ...data });
    } catch (error) {
      toast.error('Failed to get embed code');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="lead-capture-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lead Capture Forms</h1>
          <p className="text-gray-600 mt-1">Create embeddable forms to capture leads from your website</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
          data-testid="create-form-btn"
        >
          <Plus className="w-5 h-5" />
          Create Form
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200 mb-6">
        {['forms', 'webhooks', 'settings'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 px-1 font-medium capitalize transition ${
              activeTab === tab
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Forms Tab */}
      {activeTab === 'forms' && (
        <div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
            </div>
          ) : forms.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <Code className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No forms yet</h3>
              <p className="text-gray-600 mb-6">Create your first lead capture form to start collecting leads</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                <Plus className="w-5 h-5" />
                Create Form
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {forms.map(form => (
                <div
                  key={form.id}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition"
                  data-testid={`form-card-${form.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">{form.name}</h3>
                      <p className="text-sm text-gray-500">{form.fields?.length || 0} fields</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      form.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {form.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="text-sm text-gray-600 mb-4">
                    <span className="font-medium">{form.submissions_count || 0}</span> submissions
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedForm(form)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <Settings className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => openEmbedModal(form)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200"
                    >
                      <Code className="w-4 h-4" />
                      Embed
                    </button>
                    <button
                      onClick={() => deleteForm(form.id)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Webhooks Tab */}
      {activeTab === 'webhooks' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Webhook Endpoints</h2>
          <p className="text-gray-600 mb-6">Use these endpoints to send leads from external sources</p>

          <div className="space-y-4">
            <WebhookEndpoint
              title="Zapier / Make / Pabbly"
              endpoint={`${API_URL}/api/leads/capture/webhook/zapier/{workspace_id}`}
              method="POST"
              fields={['name', 'phone', 'email', 'source']}
            />
            <WebhookEndpoint
              title="Facebook / Instagram Lead Ads"
              endpoint={`${API_URL}/api/leads/capture/webhook/meta/{workspace_id}`}
              method="POST"
              fields={['Meta Lead Ads format']}
            />
            <WebhookEndpoint
              title="Custom Integration"
              endpoint={`${API_URL}/api/leads/capture/{workspace_id}`}
              method="POST"
              fields={['name (required)', 'phone', 'email', 'service', 'budget', 'message', 'source']}
            />
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Lead Capture Settings</h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Form Styling
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Primary Color</label>
                  <input type="color" defaultValue="#0a5c44" className="w-full h-10 rounded border" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Background Color</label>
                  <input type="color" defaultValue="#ffffff" className="w-full h-10 rounded border" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Success Redirect URL (optional)
              </label>
              <input
                type="url"
                placeholder="https://example.com/thank-you"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {(showCreateModal || selectedForm) && (
        <FormBuilderModal
          form={selectedForm}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedForm(null);
          }}
          onSave={() => {
            fetchForms();
            setShowCreateModal(false);
            setSelectedForm(null);
          }}
        />
      )}

      {/* Embed Code Modal */}
      {embedModal && (
        <EmbedCodeModal
          form={embedModal}
          onClose={() => setEmbedModal(null)}
          copyToClipboard={copyToClipboard}
        />
      )}
    </div>
  );
}

function WebhookEndpoint({ title, endpoint, method, fields }) {
  const copyEndpoint = () => {
    navigator.clipboard.writeText(endpoint);
    toast.success('Endpoint copied!');
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-gray-900">{title}</h3>
        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">{method}</span>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm text-gray-700 truncate">
          {endpoint}
        </code>
        <button
          onClick={copyEndpoint}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
        >
          <Copy className="w-4 h-4" />
        </button>
      </div>
      <div className="text-xs text-gray-500">
        <span className="font-medium">Fields: </span>
        {fields.join(', ')}
      </div>
    </div>
  );
}

function FormBuilderModal({ form, onClose, onSave }) {
  const [name, setName] = useState(form?.name || '');
  const [fields, setFields] = useState(form?.fields || DEFAULT_FIELDS);
  const [buttonText, setButtonText] = useState(form?.button_text || 'Submit');
  const [successMessage, setSuccessMessage] = useState(form?.success_message || 'Thank you! We will contact you soon.');
  const [styles, setStyles] = useState(form?.styles || {
    primary_color: '#0a5c44',
    background_color: '#ffffff',
    text_color: '#1f2937',
    border_radius: '8px'
  });
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      required: false,
      placeholder: ''
    };
    setFields([...fields, newField]);
  };

  const updateField = (index, updates) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFields(newFields);
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const moveField = (from, to) => {
    if (to < 0 || to >= fields.length) return;
    const newFields = [...fields];
    const [moved] = newFields.splice(from, 1);
    newFields.splice(to, 0, moved);
    setFields(newFields);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Form name is required');
      return;
    }
    if (fields.length === 0) {
      toast.error('Add at least one field');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = form
        ? `${API_URL}/api/leads/capture/forms/${form.id}`
        : `${API_URL}/api/leads/capture/forms`;
      const method = form ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name,
          fields,
          button_text: buttonText,
          success_message: successMessage,
          styles
        })
      });

      const data = await res.json();
      if (data.success) {
        toast.success(form ? 'Form updated!' : 'Form created!');
        onSave();
      } else {
        toast.error(data.detail || 'Failed to save form');
      }
    } catch (error) {
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="form-builder-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {form ? 'Edit Form' : 'Create Lead Capture Form'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Steps */}
        <div className="px-6 py-3 border-b border-gray-100 flex gap-4">
          {[1, 2, 3].map(s => (
            <button
              key={s}
              onClick={() => setStep(s)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                step === s ? 'bg-emerald-100 text-emerald-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
                step === s ? 'bg-emerald-600 text-white' : 'bg-gray-200'
              }`}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </span>
              {s === 1 ? 'Basic Info' : s === 2 ? 'Form Fields' : 'Styling'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="space-y-6 max-w-xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Form Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Website Contact Form"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="form-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                  placeholder="Submit"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Success Message</label>
                <textarea
                  value={successMessage}
                  onChange={(e) => setSuccessMessage(e.target.value)}
                  rows={3}
                  placeholder="Thank you! We'll contact you soon."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-gray-900">Form Fields</h3>
                <button
                  onClick={addField}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200"
                >
                  <Plus className="w-4 h-4" />
                  Add Field
                </button>
              </div>

              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex flex-col gap-1 pt-2">
                      <button
                        onClick={() => moveField(index, index - 1)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        disabled={index === 0}
                      >
                        <GripVertical className="w-4 h-4 rotate-180" />
                      </button>
                      <button
                        onClick={() => moveField(index, index + 1)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        disabled={index === fields.length - 1}
                      >
                        <GripVertical className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="text-xs text-gray-500">Label</label>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => updateField(index, { label: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Type</label>
                        <select
                          value={field.type}
                          onChange={(e) => updateField(index, { type: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          {FIELD_TYPES.map(ft => (
                            <option key={ft.value} value={ft.value}>{ft.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Placeholder</label>
                        <input
                          type="text"
                          value={field.placeholder || ''}
                          onChange={(e) => updateField(index, { placeholder: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div className="flex items-end gap-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateField(index, { required: e.target.checked })}
                            className="rounded border-gray-300 text-emerald-600"
                          />
                          Required
                        </label>
                      </div>
                    </div>

                    <button
                      onClick={() => removeField(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg mt-4"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {fields.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No fields added. Click "Add Field" to start.
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="font-medium text-gray-900">Form Styling</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={styles.primary_color}
                      onChange={(e) => setStyles({ ...styles, primary_color: e.target.value })}
                      className="w-12 h-12 rounded border cursor-pointer"
                    />
                    <input
                      type="text"
                      value={styles.primary_color}
                      onChange={(e) => setStyles({ ...styles, primary_color: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={styles.background_color}
                      onChange={(e) => setStyles({ ...styles, background_color: e.target.value })}
                      className="w-12 h-12 rounded border cursor-pointer"
                    />
                    <input
                      type="text"
                      value={styles.background_color}
                      onChange={(e) => setStyles({ ...styles, background_color: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={styles.text_color}
                      onChange={(e) => setStyles({ ...styles, text_color: e.target.value })}
                      className="w-12 h-12 rounded border cursor-pointer"
                    />
                    <input
                      type="text"
                      value={styles.text_color}
                      onChange={(e) => setStyles({ ...styles, text_color: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Border Radius</label>
                  <select
                    value={styles.border_radius}
                    onChange={(e) => setStyles({ ...styles, border_radius: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="0px">Square</option>
                    <option value="4px">Slightly Rounded</option>
                    <option value="8px">Rounded</option>
                    <option value="12px">More Rounded</option>
                    <option value="16px">Very Rounded</option>
                  </select>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-4">Preview</h3>
                <div
                  className="p-6 rounded-xl shadow-lg"
                  style={{
                    backgroundColor: styles.background_color,
                    borderRadius: styles.border_radius
                  }}
                >
                  {fields.map((field, index) => (
                    <div key={field.id} className="mb-4">
                      <label
                        className="block text-sm font-medium mb-1"
                        style={{ color: styles.text_color }}
                      >
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea
                          placeholder={field.placeholder}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 text-sm"
                          style={{ borderRadius: styles.border_radius }}
                          disabled
                        />
                      ) : (
                        <input
                          type={field.type}
                          placeholder={field.placeholder}
                          className="w-full px-3 py-2 border border-gray-300 text-sm"
                          style={{ borderRadius: styles.border_radius }}
                          disabled
                        />
                      )}
                    </div>
                  ))}
                  <button
                    className="w-full py-3 text-white font-medium mt-2"
                    style={{
                      backgroundColor: styles.primary_color,
                      borderRadius: styles.border_radius
                    }}
                    disabled
                  >
                    {buttonText}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            {step > 1 ? 'Back' : 'Cancel'}
          </button>
          <div className="flex gap-3">
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                data-testid="save-form-btn"
              >
                {saving ? 'Saving...' : form ? 'Update Form' : 'Create Form'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmbedCodeModal({ form, onClose, copyToClipboard }) {
  const [activeEmbedTab, setActiveEmbedTab] = useState('widget');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="embed-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Embed "{form.name}"</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 flex gap-2">
          {['widget', 'iframe', 'link', 'qr'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveEmbedTab(tab)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition capitalize ${
                activeEmbedTab === tab ? 'bg-emerald-100 text-emerald-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {tab === 'widget' && <Code className="w-4 h-4" />}
              {tab === 'iframe' && <ExternalLink className="w-4 h-4" />}
              {tab === 'link' && <ExternalLink className="w-4 h-4" />}
              {tab === 'qr' && <QrCode className="w-4 h-4" />}
              {tab === 'qr' ? 'QR Code' : tab}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeEmbedTab === 'widget' && (
            <div>
              <p className="text-gray-600 mb-4">
                Copy and paste this code into your website to embed the form.
              </p>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm max-h-96">
                  {form.widget_code}
                </pre>
                <button
                  onClick={() => copyToClipboard(form.widget_code, 'Widget code')}
                  className="absolute top-3 right-3 p-2 bg-gray-700 text-white rounded hover:bg-gray-600"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {activeEmbedTab === 'iframe' && (
            <div>
              <p className="text-gray-600 mb-4">
                Use this iframe code to embed the form in any HTML page.
              </p>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                  {form.iframe_code}
                </pre>
                <button
                  onClick={() => copyToClipboard(form.iframe_code, 'iFrame code')}
                  className="absolute top-3 right-3 p-2 bg-gray-700 text-white rounded hover:bg-gray-600"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {activeEmbedTab === 'link' && (
            <div>
              <p className="text-gray-600 mb-4">
                Share this direct link to the standalone form page.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={form.standalone_url}
                  readOnly
                  className="flex-1 px-4 py-3 bg-gray-100 border border-gray-200 rounded-lg"
                />
                <button
                  onClick={() => copyToClipboard(form.standalone_url, 'Link')}
                  className="p-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <a
                  href={form.standalone_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
          )}

          {activeEmbedTab === 'qr' && (
            <div className="text-center">
              <p className="text-gray-600 mb-6">
                Scan this QR code to open the form on mobile devices.
              </p>
              <div className="inline-block p-4 bg-white border-2 border-gray-200 rounded-xl">
                <img
                  src={form.qr_code_url}
                  alt="QR Code"
                  className="w-64 h-64"
                />
              </div>
              <div className="mt-4">
                <a
                  href={form.qr_code_url}
                  download={`qr-${form.id}.png`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Download QR Code
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
